import { CatalogProductsData, FilterData, StepFilterProperty } from '@/entities/catalog-old'

import { apiSlice } from '@/shared/api/apiSlice'
import { ServerResponse } from '@/shared/types/common'
import { TServerSection } from '@/shared/types/deprecated-types'

import { SeoLink } from '../types/type'

import { fetchCatalogProductsWithReviews, GetProductReqProps } from './catalog-products-query'

interface GetCatalogDataProps {
	categoryCode: string
	resolvedUrl: string
	regionCookies?: string
	requestCookies?: string
}

interface GetCatalogFilterUrl {
	categoryCode: string
	filterUrl: string
}

interface GetCatalogStepFilterProps {
	categoryCode: string
	filterUrl: string
	regionCookies?: string
	requestCookies?: string
}

interface GetSeoLinksData {
	RELATED_LINK_TOP: SeoLink[]
	RELATED_LINK_BOTTOM: SeoLink[]
}

interface GetSeoLinksProps {
	uri: string
}

export interface GetCatalogCategoryRequest {
	category: string
}

export interface GetCatalogCategoryResponse
	extends Pick<TServerSection, 'ID' | 'NAME' | 'DESCRIPTION' | 'PICTURE' | 'SEO'> {}

export const catalogApiSlice = apiSlice.injectEndpoints({
	endpoints: build => ({
		getMainCatalogProducts: build.query<CatalogProductsData, GetProductReqProps>({
			queryFn: (args, _api, _extraOptions, baseQuery) => fetchCatalogProductsWithReviews(args, baseQuery),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${JSON.stringify(queryArgs.params)}-${queryArgs.filterUrl}-${
					queryArgs.regionCookies || ''
				}`
			}
		}),
		getCatalogFilter: build.query<FilterData, GetCatalogDataProps>({
			query: ({ categoryCode, resolvedUrl, regionCookies = '', requestCookies }) => ({
				url: `index/catalog/filter/${categoryCode}`,
				params: {
					['sef-url']: resolvedUrl
				},
				headers: {
					Cookie: requestCookies || regionCookies
				}
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${JSON.stringify(queryArgs)}`
			},
			transformResponse: (result: ServerResponse<FilterData>) => result.data
		}),
		getCatalogStepFilter: build.query<StepFilterProperty[], GetCatalogStepFilterProps>({
			query: ({ categoryCode, filterUrl, regionCookies = '', requestCookies }) => ({
				url: `index/catalog/step-filter/${categoryCode}`,
				params: {
					filter: filterUrl
				},
				headers: {
					Cookie: requestCookies || regionCookies
				}
			}),
			serializeQueryArgs: ({ endpointName, queryArgs }) => {
				return `${endpointName}-${queryArgs.categoryCode}-${queryArgs.filterUrl}-${queryArgs.regionCookies || ''}`
			},
			transformResponse: (result: ServerResponse<StepFilterProperty[]>) => result.data
		}),
		getCatalogFilterUrl: build.query<string, GetCatalogFilterUrl>({
			query: ({ categoryCode, filterUrl }) => ({
				url: `index/catalog/filter/${categoryCode}/sef-url`,
				params: {
					filter: filterUrl
				}
			}),
			transformResponse: (result: ServerResponse<string>) => result.data
		}),
		getSeoLinks: build.query<GetSeoLinksData, GetSeoLinksProps>({
			query: ({ uri }) => ({
				url: `category/link`,
				params: {
					uri: uri
				}
			}),
			transformResponse: (result: { data: GetSeoLinksData }) => result['data']
		}),
		getCatalogCategory: build.query<GetCatalogCategoryResponse, GetCatalogCategoryRequest>({
			query: ({ category }) => `categories/${category}`,
			transformResponse: (result: ServerResponse<GetCatalogCategoryResponse>) => result['data']
		})
	})
})

export const {
	useLazyGetMainCatalogProductsQuery,
	useLazyGetCatalogFilterQuery,
	useGetCatalogFilterQuery,
	useGetMainCatalogProductsQuery,
	useLazyGetCatalogFilterUrlQuery,
	useGetCatalogStepFilterQuery,

	useLazyGetSeoLinksQuery,

	useGetCatalogCategoryQuery,

	util: { getRunningQueriesThunk },
	endpoints: { getMainCatalogProducts, getCatalogFilter }
} = catalogApiSlice

type AppDispatch = (action: any) => any

export const prefetchCatalogFilter = (dispatch: AppDispatch, args: GetCatalogDataProps) =>
	dispatch(catalogApiSlice.endpoints.getCatalogFilter.initiate(args)).unwrap()

export const prefetchCatalogProducts = (dispatch: AppDispatch, args: GetProductReqProps) =>
	dispatch(catalogApiSlice.endpoints.getMainCatalogProducts.initiate(args)).unwrap()

export const prefetchSeoLinks = (dispatch: AppDispatch, args: GetSeoLinksProps) =>
	dispatch(catalogApiSlice.endpoints.getSeoLinks.initiate(args))

export const prefetchCatalogCategory = (dispatch: AppDispatch, args: GetCatalogCategoryRequest) =>
	dispatch(catalogApiSlice.endpoints.getCatalogCategory.initiate(args))

export const prefetchCatalogStepFilter = (dispatch: AppDispatch, args: GetCatalogStepFilterProps) =>
	dispatch(catalogApiSlice.endpoints.getCatalogStepFilter.initiate(args)).unwrap()
